import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { StatusDotLg, ArcGauge, ComplianceTable } from "../components/acms/Shared";
import { StatusCircle } from "../components/acms/Indicators";
import { Badge } from "../components/ui/badge";

function Card({ title, children, testid }) {
  return (
    <div className="glass p-5 rounded-lg" data-testid={testid}>
      <h3 className="text-xs uppercase tracking-wider text-gray-600 font-bold mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function VentilationPage() {
  const { selectedSite, sites } = useSiteContext();
  const [tele, setTele] = useState(null);
  const [fans, setFans] = useState([]);
  const [ventilationValves, setVentilationValves] = useState([]);
  const [liveValues, setLiveValues] = useState({});

  useEffect(() => {
    if (!selectedSite) return;
    const load = () => {
      api.get(`/telemetry/${selectedSite}`).then((r) => setTele(r.data)).catch(() => {});
      api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "fan" } }).then((r) => setFans(r.data || [])).catch(() => {});
      api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "valve", system: "ventilation" } }).then((r) => setVentilationValves(r.data || [])).catch(() => {});
      api.get(`/assets/live-values`, { params: { site_id: selectedSite } }).then((r) => setLiveValues(r.data || {})).catch(() => {});
    };
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [selectedSite]);

  if (!tele) return <div className="text-gray-500">Loading ventilation telemetry…</div>;
  const site = sites.find((s) => s.site_id === selectedSite);
  const gw = `GW-${selectedSite.slice(-6).toUpperCase()}`;

  return (
    <div className="space-y-6" data-testid="ventilation-page">
      <div>
        <div className="text-xs text-gray-500">Dashboard → {site?.name} → Ventilation System</div>
        <h1 className="text-3xl md:text-4xl font-extrabold">Ventilation System</h1>
        <p className="text-sm text-gray-500 mt-1">Pressurization · smoke exhaust · life-safety · auto-refresh 5s</p>
      </div>

      {fans.length > 0 ? (
        <section>
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3">Registered Ventilation Fans</h2>
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            {fans.map((fan) => {
              const live = liveValues[fan.asset_id];
              const running = live ? Number(live.value) === 1 : null;
              return (
                <Card key={fan.asset_id} title={fan.name} testid={`card-fan-${fan.asset_id}`}>
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex justify-center py-1">
                      {live ? (
                        <StatusCircle status={running ? "running" : "stopped"} subtitle={running ? "Running" : "Stopped"} />
                      ) : (
                        <div className="text-center">
                          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-bold">
                            {fan.gateway_id ? "Awaiting data" : "Not wired to live data"}
                          </div>
                          <div className="text-xs text-gray-500">Static: <span className="text-gray-700 font-semibold">{fan.status || "active"}</span></div>
                        </div>
                      )}
                    </div>
                    {live && (
                      <div className="text-xs space-y-1 w-full">
                        <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Type</div><div className="font-semibold text-gray-900">{fan.meta?.fan_type || "—"}</div></div>
                        <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Location</div><div className="font-semibold text-gray-900">{fan.location_label || fan.location || "—"}</div></div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      ) : (
        <div className="glass rounded-lg p-10 text-center text-gray-500 text-sm">
          No ventilation fans registered yet. <a href="/registry/fans" className="text-orange-600 hover:text-orange-700 font-semibold">Add fans in Registry</a>
        </div>
      )}

      {ventilationValves.length > 0 && (
        <section data-testid="ventilation-valves-section">
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3">Ventilation Valves / Dampers</h2>
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            {ventilationValves.map((v) => {
              const live = liveValues[v.asset_id];
              const open = live ? Number(live.value) === 1 : null;
              return (
                <Card key={v.asset_id} title={v.name} testid={`card-vent-valve-${v.asset_id}`}>
                  <div className="flex items-center gap-4">
                    <StatusDotLg status={open === null ? "fault" : open ? "open" : "closed"} />
                    <div>
                      <div className="text-xl font-extrabold uppercase">
                        {open === null ? (v.gateway_id ? "Awaiting data" : "Not linked") : open ? "Open" : "Closed"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{v.location_label || "—"} · {v.meta?.type || "—"}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {fans.length > 0 && (
        <ComplianceTable
          title="Section E — Ventilation & Life-Safety"
          rows={fans.map((fan) => {
            const live = liveValues[fan.asset_id];
            const running = live ? Number(live.value) === 1 : null;
            return {
              parameter: fan.name,
              current: live ? (running ? "Running" : "Stopped") : "—",
              required: "Operational",
              ok: live ? running : fan.status === "active"
            };
          })}
        />
      )}
    </div>
  );
}
